/**
 * HTML sanitization using DOMPurify.
 * - Browser: uses DOMPurify with window (CRA runs client-side)
 * - SSR/Node: import sanitizeHtml from './sanitizeHtml.server' (uses JSDOM)
 *
 * Config: explicit allow-lists for tags/attrs, forbid event handlers (on*),
 * restrict URIs to safe protocols (https?, mailto, data:image/).
 */

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
    'p', 'div', 'span', 'br', 'a', 'img', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'u',
    'blockquote', 'hr', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'details', 'summary', 'sub', 'sup', 'pre', 'code',
];

const ALLOWED_ATTR = [
    'href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel',
    'width', 'height', 'border', 'cellpadding', 'cellspacing', 'align',
];

// ALLOWED_ATTR excludes event handlers (on*); ALLOWED_URI_REGEXP restricts protocols
const SANITIZE_CONFIG = {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: /^(https?|mailto|data:image\/)/i,
};

// Pre-pass: strip tracking pixels (Story 14.2)
const TRACKING_PIXEL_RE = /<img[^>]*src=["'][^"']*\/track\/[^"']*pixel\.png[^"']*["'][^>]*>/gi;

/**
 * Sanitizes HTML for safe rendering.
 * @param {string} html - Raw HTML string
 * @returns {string} Sanitized HTML
 */
export function sanitizeHtml(html) {
    if (!html || typeof html !== 'string') return '';
    const withoutTracking = html.replace(TRACKING_PIXEL_RE, '');
    return DOMPurify.sanitize(withoutTracking, SANITIZE_CONFIG);
}
